<template>
    <!-- Desktop: Vertical Layout (lg and above) -->
    <li
        :class="[stepIndex !== totalSteps - 1 ? 'pb-10' : '', 'relative hidden lg:block']"
        data-spec-class="base_progressbar_step_item-desktop"
        :data-spec-id="`base_progressbar_step_item-desktop--${stepIndex}`"
    >
        <template v-if="status === StepStatus.Complete">
            <div
                v-if="stepIndex !== totalSteps - 1"
                class="absolute top-3 right-4 mt-0.5 -mr-px h-full w-0.5 bg-brand"
                aria-hidden="true"
            />

            <a
                :class="[
                    'group relative flex items-center justify-end transition-ease',
                    disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                ]"
                data-spec-class="base_progressbar_step_item-desktop--complete-link"
                :data-spec-id="`base_progressbar_step_item-desktop--complete-link-${stepIndex}`"
                @click="!disabled && $emit('click')"
            >
                <span class="flex min-w-0 flex-1 flex-col items-end pr-2">
                    <span
                        class="max-w-[120px] text-right font-medium break-normal text-primary transition-ease hover:text-secondary xl:max-w-[140px] 2xl:max-w-[160px]"
                        data-spec-class="base_progressbar_step_item-desktop--complete-label"
                        :data-spec-id="`base_progressbar_step_item-desktop--complete-label-${stepIndex}`"
                        >{{ label }}</span
                    >
                    <span
                        v-if="description"
                        class="max-w-[120px] text-right break-normal text-tertiary xl:max-w-[140px] 2xl:max-w-[160px]"
                        >{{ description }}</span
                    >
                </span>
                <span class="ml-1 flex h-9 shrink-0 items-center">
                    <span
                        class="relative z-10 flex size-8 items-center justify-center rounded-full bg-brand transition-ease group-hover:bg-brand--light"
                    >
                        <CheckIcon
                            class="size-5 text-primary--inverse"
                            aria-hidden="true"
                        />
                    </span>
                </span>
            </a>
        </template>

        <template v-else-if="status === StepStatus.Current">
            <div
                v-if="stepIndex !== totalSteps - 1"
                class="absolute top-3 right-4 mt-0.5 -mr-px h-full w-0.5 bg-muted"
                aria-hidden="true"
            />

            <a
                class="group relative flex items-center justify-end transition-ease"
                aria-current="step"
                data-spec-class="base_progressbar_step_item-desktop--current-link"
                :data-spec-id="`base_progressbar_step_item-desktop--current-link-${stepIndex}`"
            >
                <span class="flex min-w-0 flex-1 flex-col items-end pr-2">
                    <span
                        class="max-w-[120px] text-right font-bold break-normal text-brand xl:max-w-[140px] 2xl:max-w-[160px]"
                        data-spec-class="base_progressbar_step_item-desktop--current-label"
                        :data-spec-id="`base_progressbar_step_item-desktop--current-label-${stepIndex}`"
                        >{{ label }}</span
                    >
                    <span
                        v-if="description"
                        class="max-w-[120px] text-right break-normal text-tertiary xl:max-w-[140px] 2xl:max-w-[160px]"
                        >{{ description }}</span
                    >
                </span>
                <span
                    class="ml-1 flex h-9 shrink-0 items-center"
                    aria-hidden="true"
                >
                    <span
                        class="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-brand bg-surface"
                    >
                        <span class="size-2.5 rounded-full bg-brand" />
                    </span>
                </span>
            </a>
        </template>

        <template v-else>
            <div
                v-if="stepIndex !== totalSteps - 1"
                class="absolute top-3 right-4 mt-0.5 -mr-px h-full w-0.5 bg-muted"
                aria-hidden="true"
            />

            <a
                :class="[
                    'group relative flex items-center justify-end transition-ease',
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                ]"
                data-spec-class="base_progressbar_step_item-desktop--incomplete-link"
                :data-spec-id="`base_progressbar_step_item-desktop--incomplete-link-${stepIndex}`"
                @click="!disabled && $emit('click')"
            >
                <span class="flex min-w-0 flex-1 flex-col items-end pr-2">
                    <span
                        :class="[
                            'max-w-[120px] text-right font-medium break-normal text-tertiary transition-ease xl:max-w-[140px] 2xl:max-w-[160px]',
                            disabled ? 'cursor-not-allowed !text-tertiary' : 'cursor-pointer',
                        ]"
                        data-spec-class="base_progressbar_step_item-desktop--incomplete-label"
                        :data-spec-id="`base_progressbar_step_item-desktop--incomplete-label-${stepIndex}`"
                        >{{ label }}</span
                    >
                    <span
                        v-if="description"
                        class="max-w-[120px] text-right break-normal text-secondary xl:max-w-[140px] 2xl:max-w-[160px]"
                        >{{ description }}</span
                    >
                </span>
                <span
                    class="ml-1 flex h-9 shrink-0 items-center"
                    aria-hidden="true"
                >
                    <span
                        class="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-muted bg-surface transition-ease"
                    >
                        <span
                            :class="[
                                'size-2.5 rounded-full bg-transparent transition-ease',
                                !disabled && 'group-hover:bg-muted',
                            ]"
                        />
                    </span>
                </span>
            </a>
        </template>
    </li>

    <!-- Mobile/Tablet: Horizontal Layout (below lg) -->
    <li
        class="lg:hidden"
        data-spec-class="base_progressbar_step_item-mobile"
        :data-spec-id="`base_progressbar_step_item-mobile--${stepIndex}`"
    >
        <a
            v-if="status === StepStatus.Complete"
            :class="[
                'block size-3 rounded-full bg-brand transition-ease',
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            ]"
            @click="!disabled && $emit('click')"
        >
            <span class="sr-only">{{ label }}</span>
        </a>
        <a
            v-else-if="status === StepStatus.Current"
            :class="[
                'relative flex items-center justify-center transition-ease',
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            ]"
            aria-current="step"
            @click="!disabled && $emit('click')"
        >
            <span
                class="absolute flex size-5 p-px"
                aria-hidden="true"
            >
                <span class="size-full rounded-full bg-brand" />
            </span>
            <span
                class="relative block size-3 rounded-full bg-brand"
                aria-hidden="true"
            />
        </a>
        <a
            v-else
            :class="[
                'block size-3 rounded-full bg-muted transition-ease',
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            ]"
            @click="!disabled && $emit('click')"
        >
        </a>
    </li>
</template>

<script setup lang="ts">
import { CheckIcon } from '@heroicons/vue/20/solid';
import { StepStatus } from '@/types';

interface Props {
    label?: string;
    description?: string;
    status: StepStatus;
    disabled?: boolean;
    stepIndex: number;
    totalSteps: number;
}

withDefaults(defineProps<Props>(), {
    label: undefined,
    description: undefined,
    disabled: false,
});

defineEmits<{
    (e: 'click'): void;
}>();
</script>
